import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import LogoIconDecoration from './LogoIconDecoration';

export default function Footer() {
  const [contactInfo, setContactInfo] = useState({
    company_name: 'Site Facility ApS',
    address: 'Vestkraftgade 1, 2nd Floor, 6700 Esbjerg, Denmark',
    phone: '+45 21 79 57 53',
    email: 'jh@sitefacility.dk',
    contact_person: 'Jakob Krøjgaard Heskjær',
    cvr: '40030964',
  });

  useEffect(() => {
    async function loadContact() {
      const data = await fetchCollection('contact_information');
      if (data && data.length > 0) {
        setContactInfo(data[0]);
      }
    }
    loadContact();
  }, []);

  return (
    <footer className="bg-primary text-white pt-16 pb-8 border-t border-slate-800 relative overflow-hidden">
      {/* Background Decoration */}
      <LogoIconDecoration 
        className="w-96 h-96 opacity-[0.02] -right-16 -bottom-16 rotate-12" 
        style={{ filter: 'brightness-0 invert' }}
      />
      <LogoIconDecoration 
        className="w-64 h-64 opacity-[0.01] -left-12 -top-12 -rotate-12" 
        style={{ filter: 'brightness-0 invert' }}
      />

      <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12 relative z-10">
        {/* Brand Column */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/sitefacility-0-1_1.png" 
              alt="Site Facility Logo" 
              className="h-10 md:h-12 w-auto object-contain" 
              style={{ filter: 'invert(1) hue-rotate(180deg)' }}
            />
          </div>
          <p className="text-sm text-slate-300 leading-relaxed">
            Tailored accommodation solutions, camps, hotel management, floatels, and site offices for large-scale projects worldwide.
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-display text-sm font-bold tracking-wider text-accent uppercase mb-4">Quick Links</h4>
          <ul className="space-y-2.5 text-sm text-slate-300">
            <li>
              <Link to="/" className="hover:text-accent transition-colors duration-200">Home</Link>
            </li>
            <li>
              <Link to="/services" className="hover:text-accent transition-colors duration-200">Services</Link>
            </li>
            <li>
              <Link to="/references" className="hover:text-accent transition-colors duration-200">References</Link>
            </li>
            <li>
              <Link to="/cases" className="hover:text-accent transition-colors duration-200">Cases</Link>
            </li>
            <li>
              <Link to="/about" className="hover:text-accent transition-colors duration-200">About Us</Link>
            </li>
            <li>
              <Link to="/privacy-policy" className="hover:text-accent transition-colors duration-200">Privacy Policy</Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-accent transition-colors duration-200">Contact</Link>
            </li>
          </ul>
        </div>

        {/* Services */}
        <div>
          <h4 className="font-display text-sm font-bold tracking-wider text-accent uppercase mb-4">Services</h4>
          <ul className="space-y-2 text-sm text-slate-300">
            <li>
              <Link to="/services/floatels" className="hover:text-accent transition-colors duration-200">Floatels</Link>
            </li>
            <li>
              <Link to="/services/camps" className="hover:text-accent transition-colors duration-200">Camps</Link>
            </li>
            <li>
              <Link to="/services/hotel-management" className="hover:text-accent transition-colors duration-200">Hotel Management</Link>
            </li>
            <li>
              <Link to="/services/site-office" className="hover:text-accent transition-colors duration-200">Site Offices</Link>
            </li>
            <li>
              <Link to="/services/catering" className="hover:text-accent transition-colors duration-200">Catering</Link>
            </li>
            <li>
              <Link to="/services/housekeeping" className="hover:text-accent transition-colors duration-200">Housekeeping</Link>
            </li>
            <li>
              <Link to="/services/laundry" className="hover:text-accent transition-colors duration-200">Laundry</Link>
            </li>
            <li>
              <Link to="/services/personnel-transfer-baskets" className="hover:text-accent transition-colors duration-200">Personnel Transfer Baskets</Link>
            </li>
          </ul>
        </div>

        {/* Contact Info */}
        <div>
          <h4 className="font-display text-sm font-bold tracking-wider text-accent uppercase mb-4">Contact Info</h4>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex items-start gap-2.5">
              <i className="fa-regular fa-location-dot text-accent mt-1" />
              <span>{contactInfo.address}</span>
            </li>
            <li className="flex items-start gap-2.5">
              <i className="fa-regular fa-phone text-accent mt-1" />
              <div className="flex flex-col gap-1">
                {contactInfo.phone.split('/').map((p, idx) => (
                  <a 
                    key={idx}
                    href={`tel:${p.trim().replace(/\s+/g, '')}`} 
                    className="hover:text-accent transition-colors duration-200 whitespace-nowrap"
                  >
                    {p.trim()}
                  </a>
                ))}
              </div>
            </li>
            <li className="flex items-center gap-2.5">
              <i className="fa-regular fa-envelope text-accent" />
              <a href={`mailto:${contactInfo.email}`} className="hover:text-accent transition-colors duration-200 whitespace-nowrap">
                {contactInfo.email}
              </a>
            </li>
            <li className="flex items-start gap-2.5">
              <i className="fa-regular fa-user text-accent mt-1" />
              <div>
                <span className="block text-xs text-slate-400">Contact Person</span>
                <span className="font-medium">{contactInfo.contact_person}</span>
              </div>
            </li>
            <li className="text-xs text-slate-400 pt-1">
              CVR: {contactInfo.cvr}
            </li>
          </ul>
        </div>
      </div>

      {/* Copyright */}
      <div className="mx-auto max-w-7xl px-6 md:px-12 pt-8 border-t border-slate-800 text-center text-xs text-slate-400 relative z-10">
        <p>Copyright &copy; {new Date().getFullYear()} {contactInfo.company_name}. All rights reserved.</p>
      </div>
    </footer>
  );
}
